import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Icon, ICONS } from './icon';
import { NavLink } from './nav-link';
import { COLLECTION_TOURS_LIMIT, HOME_CATEGORIES_SEED_KEY, collectionsOfKind, type CategoryStat } from '../lib/collections';
import { localePath } from '../lib/format';
import { useLocale } from '@todo/core/i18n/use-locale';
import { LIST_BUCKET, toursRpcArgs } from '@todo/core/list-queries';
import { getPrefetched } from '@todo/core/static-prefetch';
import { supabase } from '@todo/core/supabase';
import { sizedTourImage } from '@todo/core/tours';
import { SITE } from '@site';

import css from './category-guide.module.css';

// The home page's guide to the category collections: per category the top
// tour's photo, how many experiences its page lists, a paragraph of copy
// (`home.guide.<slug>`) and a keyword link to it. The stats come from the
// build-time seed (HOME_CATEGORIES_SEED_KEY). Without one — a client-side
// navigation to the home page, whose seed isn't in the document, or the dev
// server — the cards render without photo and count first, and fetchStats
// fills them in after mount.
export function CategoryGuide() {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const [stats, setStats] = useState(() => getPrefetched<CategoryStat[]>(LIST_BUCKET, HOME_CATEGORIES_SEED_KEY) ?? []);

  useEffect(() => {
    if (stats.length) return;
    let cancelled = false;
    fetchStats()
      .then((rows) => {
        if (!cancelled) setStats(rows);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [stats.length]);

  return (
    <ul className={css.grid}>
      {collectionsOfKind('category').map((c) => {
        const stat = stats.find((s) => s.slug === c.slug);
        const href = localePath(locale, c.slug);
        // Phones show an 88 px strip of the photo: 360x240 is plenty there.
        const image = stat?.image ? sizedTourImage(stat.image, 360, 240) : null;
        const imageDesktop = stat?.image ? sizedTourImage(stat.image, 480, 320) : null;
        return (
          <li key={c.slug} className={css.card}>
            {image ? (
              <picture className={css.picture}>
                {imageDesktop ? <source media="(min-width: 1024px)" srcSet={imageDesktop} /> : null}
                <img className={css.photo} src={image} alt="" width={360} height={240} loading="lazy" decoding="async" />
              </picture>
            ) : (
              <span className={`${css.photo} ${css.noPhoto}`} aria-hidden="true" />
            )}
            <div className={css.body}>
              {stat?.count ? <p className={css.count}>{t('home.guideCount', { count: stat.count })}</p> : null}
              <h3 className={css.title}>
                <NavLink href={href} className={css.titleLink}>
                  {t(`collections.${c.slug}.title`)}
                </NavLink>
              </h3>
              <p className={css.text}>{t(`home.guide.${c.slug}.text`)}</p>
              <NavLink href={href} className={css.more}>
                {t(`home.guide.${c.slug}.link`)} <Icon d={ICONS.arrowLong} size={16} />
              </NavLink>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

/**
 * The seed's numbers, asked in the browser: per category the same
 * tours_by_theme list the site's prefetch counts (best ranked first, up to
 * COLLECTION_TOURS_LIMIT), only its photos.
 */
async function fetchStats(): Promise<CategoryStat[]> {
  return Promise.all(
    collectionsOfKind('category').map(async (c) => {
      const { data, error } = await supabase
        .rpc('tours_by_theme', toursRpcArgs({ theme: c.theme, city: SITE.cityIds[0], limit: COLLECTION_TOURS_LIMIT }))
        .select('image_url');
      if (error) throw new Error(`tours_by_theme(${c.theme}): ${error.message}`);
      const rows = (data ?? []) as { image_url: string | null }[];
      return { slug: c.slug, count: rows.length, image: rows[0]?.image_url ?? null };
    }),
  );
}
